import { Disclosure } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import React from "react";
import { IBookingSlotsProps } from "../interface";

import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import SkeletonType1 from "@/app/components/ui/Skeletons/Type1";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useUtils } from "@/app/hooks/useUtils";

import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { ISlotsState } from "@/app_redux/reducers/slices/auth/slots_slice";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import clsx from "clsx";

const BookedSlotsComponent: React.FC<IBookingSlotsProps> = () => {
  /* Instantiations */
  const isSlotListInProgress = useLoader("isAvailableSlotsInProgress");
  const { setVisible, setModalData } = useModalActions("BOOKED_SLOTS_DETAILS");
  const { addAMPMToTime } = useUtils();
  const { BASIC } = useImageConstants();
  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("SLOTS", "id");
  const { routeState } = useRouteInfo(routeId);
  const { bookedSlots } = routeState?.state as ISlotsState;
  const { customerId, petId } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id")).routeState.state as ICustomerProfileState;
  const { execute: logClick } = useClickLogAPI();
  const vmsUserId = useAppSelector((state) => state.auth?.user?.email);

  /* APIs Hooks */

  /* Actions */
  const handleSlotSelect = (slotId: string, extra: { clinicId?: string; clinicName?: string; staffId?: string; startTime?: string } = {}) => {
    logClick({
      event_type: "BOOKED_SLOT_CLICK",
      scr_id: "BOOKING_PAGE",
      client_id: customerId,
      pet_id: petId,
      vms_user_id: vmsUserId,
      clinic_id: extra.clinicId,
      clinic_name: extra.clinicName,
      staff_id: extra.staffId,
      cta_text: extra.startTime,
      timestamp: Date.now(),
    });
    setModalData({
      slotId: slotId,
    });
    setVisible();
  };

  return (
    <div className="mb-10">
      {!isSlotListInProgress ? (
        bookedSlots?.length > 0 ? (
          <div className="w-full pt-1">
            <div className="w-full  rounded-2xl bg-white py-1 flex flex-col gap-2 mb-12 ">
              {bookedSlots?.map((slot, id) => {
                return (
                  <Disclosure key={id} defaultOpen={id === 0}>
                    {({ open }) => (
                      <>
                        <Disclosure.Button className=" px-2.5 py-3 items-center flex w-full justify-between rounded-lg bg-[#808DA01A] text-left text-sm font-medium  focus:outline-none ">
                          <div className="flex flex-row gap-16 items-center">
                            <div className="flex flex-col gap-1">
                              <span className="text-sm text-[#000]">{slot?.clinic_name}</span>
                            </div>
                          </div>
                          <ChevronDownIcon className={`${open ? "rotate-180 transform" : ""} h-5 w-5 `} />
                        </Disclosure.Button>
                        <Disclosure.Panel className="px-4 pb-2  text-sm text-gray-500 ">
                          {slot?.data?.map((doc, id) => {
                            return (
                              <div className="w-full flex items-start border-b border-solid border-gray-300 py-3" key={id}>
                                <div className="w-1/5	 flex flex-col gap-1 ">
                                  <h1 className="text-[#0068FF] text-xs font-medium	">{doc?.staff_name}</h1>
                                  <p className="text-[#808DA0] text-xs font-medium	">{doc?.expertise}</p>
                                  <p className="text-[#808DA0] text-xs font-medium	">{doc?.experience}</p>
                                  {doc?.last_visited_staff && (
                                    <button className="bg-green-800 text-white py-1 px-0 w-[100px] rounded-md ">
                                      Last Visited
                                    </button>
                                  )}
                                  {doc?.preferred_staff && (
                                    <button className="bg-blue-600 text-white py-1 px-0 w-[150px] rounded-md ">
                                      Preferred
                                    </button>
                                  )}
                                </div>
                                <div
                                  className={`flex p-4 flex-wrap items-start content-start gap-2.5 w-4/5 ${
                                    doc?.data?.length > 27 ? "overflow-auto h-36	" : ""
                                  }`}
                                >
                                  {doc?.data?.map((docSlot, idx) => {
                                    return (
                                      <button
                                        className={clsx(
                                          "border border-red-100 flex justify-between items-center gap-1 rounded-md px-4 py-2 text-red-700 shadow-sm hover:scale-105 transition-all hover:border bg-red-300",
                                        )}
                                        data-tooltip-id={`${docSlot.slot_id}`}
                                        key={`${docSlot.start_time}:${idx}`}
                                        onClick={() => {
                                          handleSlotSelect(docSlot.slot_id, {
                                            clinicId: slot?.clinic_id,
                                            clinicName: slot?.clinic_name,
                                            staffId: doc?.staff_id,
                                            startTime: docSlot?.start_time,
                                          });
                                        }}
                                      >
                                        {addAMPMToTime(docSlot?.start_time)}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </Disclosure.Panel>
                      </>
                    )}
                  </Disclosure>
                );
              })}
            </div>
          </div>
        ) : (
          <NoDataFoundComponent
            message="No Slot Found"
            imageURL={BASIC}
            description="No slots available for the given data / filters."
          />
        )
      ) : (
        <div className="mt-4 ml-4">
          <SkeletonType1 className="mt-2" />
          <SkeletonType1 className="mt-2" />
        </div>
      )}
    </div>
  );
};

export default BookedSlotsComponent;
